import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Sterling SuiteLauncher — the 9-dot suite menu.
 *
 * Reads apps + reserved-marks from a manifest (defaults to
 * `STERLING_MANIFEST`). One tile per live app + disabled "Coming soon"
 * tiles for reserved marks + a trailing Suggest CTA.
 *
 * Accessibility:
 *   - Panel = role="menu"; tiles = role="menuitem"
 *   - Reserved tiles use aria-disabled (focusable, screen reader announces "coming soon")
 *   - Esc + outside-click dismiss; focus returns to button on close
 *
 * Window-level analytics events (custom):
 *   - "sterling:launcher:opened"      { source: 'click' | 'keyboard' }
 *   - "sterling:launcher:dismissed"   { via: 'esc' | 'outside' | 'button' | 'navigate' }
 *   - "sterling:launcher:navigated"   { from, to }
 */
import { useCallback, useEffect, useId, useRef, useState, } from 'react';
import clsx from 'clsx';
import { STERLING_MANIFEST } from '../manifest/index.js';
import { MarkSVG } from './MarkSVG.js';
import { Icon } from './Icon.js';
import { sterlingNavigate } from '../navigate.js';
function dispatch(name, detail) {
    if (typeof window === 'undefined')
        return;
    window.dispatchEvent(new CustomEvent(name, { detail }));
}
function buildTiles(m) {
    const tiles = [];
    for (const [slug, app] of Object.entries(m.apps)) {
        tiles.push({
            key: `live-${slug}`,
            kind: 'live',
            appSlug: slug,
            markSlug: app.mark_slug,
            name: app.name,
            tagline: app.tagline,
        });
    }
    for (const mk of m.marks.filter(m => m.status === 'reserved')) {
        tiles.push({
            key: `reserved-${mk.slug}`,
            kind: 'reserved',
            markSlug: mk.slug,
            name: 'Reserved',
            tagline: 'Coming soon',
        });
    }
    return tiles;
}
function NineDots({ size = 18 }) {
    const positions = [];
    for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++)
            positions.push([6 + c * 6, 6 + r * 6]);
    }
    return (_jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: positions.map(([x, y], i) => _jsx("circle", { cx: x, cy: y, r: "1.5" }, i)) }));
}
export function SuiteLauncher({ currentApp, onNavigate, onSuggest, manifest = STERLING_MANIFEST, className, }) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const panelRef = useRef(null);
    const labelId = useId();
    const tiles = buildTiles(manifest);
    const liveCount = Object.keys(manifest.apps).length;
    const reservedCount = manifest.marks.filter(m => m.status === 'reserved').length;
    const totalApps = liveCount + reservedCount;
    const close = useCallback((via) => {
        setOpen(false);
        dispatch('sterling:launcher:dismissed', { via });
        requestAnimationFrame(() => btnRef.current?.focus());
    }, []);
    // Esc + outside-click dismissal
    useEffect(() => {
        if (!open)
            return;
        function onKey(e) {
            if (e.key === 'Escape') {
                e.stopPropagation();
                close('esc');
            }
        }
        function onClickOutside(e) {
            const t = e.target;
            if (panelRef.current?.contains(t) || btnRef.current?.contains(t))
                return;
            close('outside');
        }
        window.addEventListener('keydown', onKey);
        window.addEventListener('mousedown', onClickOutside);
        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('mousedown', onClickOutside);
        };
    }, [open, close]);
    function handleTile(tile) {
        if (tile.kind === 'live' && tile.appSlug) {
            dispatch('sterling:launcher:navigated', {
                from: currentApp ?? '(unknown)',
                to: tile.appSlug,
            });
            const handler = onNavigate ?? sterlingNavigate;
            handler(tile.appSlug);
            close('navigate');
        }
        else if (tile.kind === 'suggest') {
            onSuggest?.();
            close('navigate');
        }
        // Reserved tiles are no-ops.
    }
    function handleKeyDown(e, tile) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTile(tile);
        }
    }
    return (_jsxs(_Fragment, { children: [_jsx("button", { ref: btnRef, type: "button", className: clsx('sterling-suite-btn', open && 'active', className), "aria-haspopup": "menu", "aria-expanded": open, "aria-label": "Open Sterling suite", "aria-controls": labelId, onClick: e => {
                    if (open) {
                        close('button');
                    }
                    else {
                        setOpen(true);
                        const source = e.detail === 0 ? 'keyboard' : 'click';
                        dispatch('sterling:launcher:opened', { source });
                    }
                }, children: _jsx(NineDots, {}) }), _jsx("div", { className: clsx('sterling-suite-scrim', open && 'open'), "aria-hidden": "true", onClick: () => close('outside') }), _jsxs("div", { ref: panelRef, id: labelId, className: clsx('sterling-suite-panel', open && 'open'), role: "menu", "aria-label": "Sterling suite", "aria-hidden": !open, children: [_jsxs("header", { className: "sterling-suite-head", children: [_jsxs("h3", { className: "ty-panel-title", children: [manifest.platform.name, " Suite"] }), _jsxs("span", { className: "sterling-suite-meta", children: [totalApps, " apps \u00B7 ", liveCount, " live"] })] }), _jsxs("div", { className: "sterling-suite-grid", children: [tiles.map(tile => {
                                if (tile.kind === 'reserved') {
                                    return (_jsxs("div", { role: "menuitem", "aria-disabled": "true", className: "sterling-suite-tile reserved", title: "Reserved \u2014 coming soon", children: [_jsx(MarkSVG, { slug: tile.markSlug, size: 36, fg: "var(--text-disabled)", accent: "var(--text-disabled)" }), _jsx("div", { className: "sterling-suite-tile-name", children: "Reserved" }), _jsx("div", { className: "sterling-suite-tile-tag", children: "Coming soon" })] }, tile.key));
                                }
                                const isCurrent = tile.appSlug === currentApp;
                                return (_jsxs("button", { type: "button", role: "menuitem", className: clsx('sterling-suite-tile live', isCurrent && 'current'), "aria-current": isCurrent ? 'page' : undefined, title: `Open ${tile.name}`, onClick: () => handleTile(tile), onKeyDown: e => handleKeyDown(e, tile), children: [_jsx(MarkSVG, { slug: tile.markSlug, size: 36 }), _jsx("div", { className: "sterling-suite-tile-name", children: tile.name }), _jsx("div", { className: "sterling-suite-tile-tag", children: tile.tagline }), isCurrent && _jsx("span", { className: "sterling-suite-tile-dot", "aria-label": "Current app" })] }, tile.key));
                            }), _jsxs("button", { type: "button", role: "menuitem", className: "sterling-suite-tile cta", title: "Suggest a new Sterling app", onClick: () => { onSuggest?.(); close('navigate'); }, children: [_jsx("span", { className: "sterling-suite-tile-glyph", children: _jsx(Icon, { name: "plus", size: 18 }) }), _jsx("div", { className: "sterling-suite-tile-name", style: { color: 'var(--text-muted)', fontWeight: 500 }, children: "Suggest" }), _jsx("div", { className: "sterling-suite-tile-tag", children: "A new Sterling app" })] })] }), _jsxs("footer", { className: "sterling-suite-foot", children: [_jsxs("span", { className: "sterling-suite-foot-mark", children: ["Silver Falls ", _jsx("em", { children: "Sterling" })] }), _jsx("button", { type: "button", className: "sterling-suite-foot-link", children: "Manage suite \u2192" })] })] })] }));
}
//# sourceMappingURL=SuiteLauncher.js.map